import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/shared/hooks/useToast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FileUploader } from '@/shared/components/custom/file-uploader/FileUploader';
import { useImportTasks } from '../../hooks/useTaskImportMutations';
import type { ImportTasksResponse, ImportTaskError } from '../../types';

const ACCEPTED_TYPES = {
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;

interface ImportTasksDialogProps {
  projectId: string;
  boardId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportTasksDialog({
  projectId,
  boardId,
  open,
  onOpenChange,
}: ImportTasksDialogProps) {
  const { t } = useTranslation('tasks');
  const { toast } = useToast();
  const { mutate: importTasks, isPending } = useImportTasks(projectId);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportTasksResponse | null>(null);
  const [importError, setImportError] = useState(false);

  function handleFileAccepted(files: File[]) {
    setSelectedFile(files[0] ?? null);
    setResult(null);
    setImportError(false);
  }

  function handleSubmit() {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('boardId', boardId);

    importTasks(formData, {
      onSuccess: (data) => {
        const hasPartialErrors = (data.errors?.length ?? 0) > 0;
        if (!hasPartialErrors) {
          handleOpenChange(false);
          toast.success({ title: t('import.successToast', { count: data.created.length }) });
        } else {
          setResult(data);
        }
      },
      onError: () => {
        setImportError(true);
      },
    });
  }

  function handleOpenChange(value: boolean) {
    if (!value) {
      setSelectedFile(null);
      setResult(null);
      setImportError(false);
    }
    onOpenChange(value);
  }

  const hasErrors = (result?.errors?.length ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-md" data-testid="tasks-import-dialog-root">
        <DialogHeader>
          <DialogTitle>{t('import.dialogTitle')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <FileUploader
            accept={ACCEPTED_TYPES}
            maxSize={MAX_FILE_SIZE}
            onFilesAccepted={handleFileAccepted}
            disabled={isPending}
          >
            {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
              <div
                {...getRootProps()}
                data-testid="tasks-import-dialog-file-uploader"
                className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                  isDragReject
                    ? 'border-destructive bg-destructive/5'
                    : isDragActive
                      ? 'border-primary bg-primary/5'
                      : selectedFile
                        ? 'border-primary/40 bg-primary/5'
                        : 'border-muted-foreground/25 hover:border-primary/40'
                }`}
              >
                <input data-testid="tasks-import-file-input" {...getInputProps()} />
                {selectedFile ? (
                  <>
                    <FileText className="h-8 w-8 text-primary" />
                    <p className="text-sm font-medium">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(0)} KB
                    </p>
                  </>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <p className="text-sm font-medium">{t('import.fileLabel')}</p>
                    <p className="text-xs text-muted-foreground">{t('import.fileHint')}</p>
                  </>
                )}
              </div>
            )}
          </FileUploader>

          {importError && (
            <div
              className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
              data-testid="tasks-import-dialog-error"
            >
              <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
              <p className="text-sm text-destructive">{t('import.errorToast')}</p>
            </div>
          )}

          {result && <ImportResults result={result} hasErrors={hasErrors} t={t} />}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            {t('subtasks.confirmDelete.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isPending || !selectedFile}
            data-testid="tasks-import-dialog-submit-btn"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('import.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ImportResultsProps {
  result: ImportTasksResponse;
  hasErrors: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function ImportResults({ result, hasErrors, t }: ImportResultsProps) {
  const createdCount = result.created.length;
  const errorCount = result.errors.length;

  return (
    <div className="space-y-2" data-testid="tasks-import-dialog-results">
      {createdCount > 0 && (
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
          <p className="text-sm">{t('import.successToast', { count: createdCount })}</p>
        </div>
      )}

      {hasErrors && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
            <p className="text-sm text-destructive">
              {t('import.partialSuccessToast', { created: createdCount, errors: errorCount })}
            </p>
          </div>
          <ul className="max-h-32 overflow-y-auto rounded border p-2 text-xs text-muted-foreground">
            {result.errors.map((err: ImportTaskError) => (
              <li key={err.rowIndex} className="py-0.5">
                <span className="font-medium">
                  {t('import.errorRowLabel', { row: err.rowIndex })}:
                </span>{' '}
                {err.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
